/** Full pack flow for one order: tick lines → photos (required) → notes → Mark as packed. Time is auto-logged to the timesheet. */
import React, { useEffect, useState } from 'react';
import { Camera, Check, Loader2, MapPin, PackageCheck, Printer, X } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { PhotoAttach } from '@/components/timesheets/PhotoAttach';
import { BRAND, packApi, openPackingSlip } from './shared';

const MAX_PACK_PHOTOS = 8;


function stockHint(l) {
  const s = l.stock;
  if (s === undefined) return null;
  if (!s) return <span className="block text-[11px] text-gray-400" data-testid={`pack-stock-${l.key}`}>No stock match — not deducted</span>;
  if (s.deducted) return <span className="block text-[11px] text-emerald-700" data-testid={`pack-stock-${l.key}`}>Stock already deducted · {s.name}</span>;
  const short = l.qty != null && Number(l.qty) > s.on_hand;
  return <span className={`block text-[11px] ${short ? 'text-rose-600 font-semibold' : 'text-sky-700'}`} data-testid={`pack-stock-${l.key}`}>Will deduct from stock · {s.name} · {s.on_hand} on hand{short ? ' — short!' : ''}</span>;
}

function stockToast(stock) {
  if (!stock) return undefined;
  const n = stock.deducted?.length || 0;
  const bits = [];
  if (n) bits.push(`${n} line${n === 1 ? '' : 's'} deducted from stock`);
  if (stock.unmatched?.length) bits.push(`${stock.unmatched.length} not matched`);
  if (stock.deducted?.some((d) => d.short)) bits.push('some lines were short');
  return bits.join(' · ') || undefined;
}

export function PackSheet({ order, onClose, onDone }) {
  const [checked, setChecked] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(null);
  const brand = BRAND[order.product] || {};

  useEffect(() => {
    packApi('POST', `/${order.product}/${order.id}/start`).then((s) => { setStarted(s.started_at); if (s.checked?.length) setChecked(s.checked); }).catch((e) => toast.error(e.message));
  }, [order.product, order.id]);

  const toggle = (key) => {
    const next = checked.includes(key) ? checked.filter((k) => k !== key) : [...checked, key];
    setChecked(next);
    packApi('PUT', `/${order.product}/${order.id}/progress`, { checked: next }).catch(() => {});
  };
  const allTicked = order.lines.length > 0 && checked.length === order.lines.length;

  const complete = async () => {
    if (!photos.length) { toast.error('Take at least one photo of the packed order'); return; }
    setBusy(true);
    try {
      const d = await packApi('POST', `/${order.product}/${order.id}/complete`, { checked, photo_ids: photos, notes });
      const stockMsg = stockToast(d.packing?.stock);
      toast.success(`${order.reference} packed`, { description: [d.timesheet_entry ? `${d.timesheet_entry.minutes} min logged to Packing Orders` : null, stockMsg].filter(Boolean).join(' · ') || undefined });
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const cancel = () => { packApi('DELETE', `/${order.product}/${order.id}/start`).catch(() => {}); onClose(); };

  return (
    <div className="fixed inset-0 z-50 bg-slate-100 flex flex-col" data-testid="pack-sheet">
      <div className="text-white px-4 py-3 flex items-center gap-3 shrink-0" style={{ background: brand.colour || '#111827', color: brand.ink || '#fff' }}>
        <button type="button" onClick={cancel} className="p-1.5 rounded-lg hover:bg-black/10" aria-label="Close" data-testid="pack-sheet-close"><X className="w-5 h-5" /></button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{brand.label} · packing</div>
          <div className="font-mono font-bold text-lg leading-tight truncate">{order.reference}</div>
        </div>
        {started && <div className="text-xs opacity-80 text-right">Started<br /><b>{new Date(started).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</b></div>}
        <button type="button" onClick={() => openPackingSlip(order)} className="p-2 rounded-lg hover:bg-black/10" aria-label="Print packing slip" data-testid="pack-sheet-slip"><Printer className="w-5 h-5" /></button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 max-w-2xl w-full mx-auto">
        <div className="bg-white rounded-xl border p-3">
          <div className="font-semibold text-gray-900">{order.customer || order.installer_name}</div>
          {order.company_name && <div className="text-xs text-gray-500">{order.company_name}</div>}
          {order.address && <div className="text-xs text-gray-600 mt-1 flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5 shrink-0" /> {order.address}</div>}
          {order.delivery_notes && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">{order.delivery_notes}</div>}
        </div>

        <section>
          <div className="flex items-center justify-between mb-1.5">
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500">1 · Tick each item as it goes in</h2>
            <span className={`text-xs font-bold ${allTicked ? 'text-emerald-700' : 'text-gray-500'}`} data-testid="pack-tick-count">{checked.length}/{order.lines.length}</span>
          </div>
          <ul className="bg-white rounded-xl border divide-y" data-testid="pack-sheet-lines">
            {order.lines.map((l) => {
              const on = checked.includes(l.key);
              return (
                <li key={l.key}>
                  <button type="button" onClick={() => toggle(l.key)} className="w-full text-left px-3 py-3 flex items-center gap-3 active:bg-gray-50" data-testid={`pack-line-${l.key}`} aria-pressed={on}>
                    <span className={`w-7 h-7 rounded-md border-2 flex items-center justify-center shrink-0 ${on ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-gray-300 bg-white'}`}>{on && <Check className="w-4 h-4" />}</span>
                    <span className="flex-1 min-w-0">
                      <span className={`block font-semibold text-gray-900 ${on ? 'line-through text-gray-400' : ''}`}>{l.label}</span>
                      {l.detail && <span className="block text-xs text-gray-500">{l.detail}</span>}
                      {stockHint(l)}
                    </span>
                    {l.qty != null && <span className="font-mono font-bold text-lg text-gray-800 shrink-0">{l.qty}×</span>}
                  </button>
                </li>
              );
            })}
            {order.lines.length === 0 && <li className="px-3 py-3 text-sm text-gray-500">No itemised lines on this order — check the order details.</li>}
          </ul>
        </section>

        <section>
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">2 · Photograph the packed order <span className="text-rose-600">(required)</span></h2>
          <div className="bg-white rounded-xl border p-3">
            <PhotoAttach photos={photos} onChange={setPhotos} disabled={busy} max={MAX_PACK_PHOTOS} big />
            {!photos.length && <p className="text-xs text-gray-500 mt-2 flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Show the boxes/pallet and labels clearly.</p>}
          </div>
        </section>

        <section>
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">3 · Notes (optional)</h2>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={1000} placeholder="e.g. 2 boxes + 1 pallet, fragile stickers on all sides" className="bg-white text-base" data-testid="pack-notes" />
        </section>
      </div>

      <div className="shrink-0 border-t bg-white px-4 py-3 max-w-2xl w-full mx-auto">
        {!allTicked && order.lines.length > 0 && <p className="text-xs text-amber-700 mb-2" data-testid="pack-untick-warning">{order.lines.length - checked.length} line(s) not ticked — you can still mark it packed.</p>}
        <button type="button" onClick={complete} disabled={busy || !photos.length} className="w-full h-14 rounded-xl bg-emerald-600 disabled:bg-gray-300 disabled:text-gray-500 text-white font-bold text-base inline-flex items-center justify-center gap-2" data-testid="pack-complete">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <PackageCheck className="w-5 h-5" />} {photos.length ? 'Mark as packed' : 'Add a photo to continue'}
        </button>
      </div>
    </div>
  );
}
